import { ScopeButton } from '@monorepo/shared-lib';
import { Popover, Typography } from 'antd';
import * as React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import routes from 'routes';
import { getAuthUser } from 'store/auth/selectors';
import { getReturnTypeNameLabel } from 'i18n/returnTypeLabel';
import { formatDisplayDate } from 'i18n/calendarLabels';
import { MappedReturn } from 'store/returns/models/returnData';
import {
  getReturnActionLabel,
  isReturnActionPrimary,
  shouldCreateNewSubmission,
  shouldShowReturnAction,
} from 'utils/return-action-label';
import { usePwaMobileNavEnabled } from 'utils/usePwaMobileNavEnabled';
import { isPwaTaxReturnCreationAllowed } from 'utils/pwa-nav';
import { usePwaBottomSheetDragClose } from 'utils/usePwaBottomSheetDragClose';
import { useOverlayBackHandler } from 'utils/useOverlayBackHandler';
import { StatusGetter } from 'utils/status-getter';
import { ScopeTable } from './table';

const { Text } = Typography;

interface ReturnsPopoverProps {
  returns: MappedReturn[];
  children: React.ReactNode;
  maxHeight?: number;
  onClick?: () => void;
  onCreateSubmission?: (connectionId: string, mappingId: string) => void;
}

type ReturnRow = {
  key: string | number;
  returnName: string;
  organisationName: string;
  status: string;
  originalStatus?: string;
  dueDate: string;
  connectionId: string;
  mappingId: string;
  dataLogId?: string;
  returnItem: MappedReturn;
  buttonText: string;
  isCreate: boolean;
};

type PwaReturnsSheetProps = {
  title: string;
  closeLabel: string;
  pullDownLabel: string;
  onClose: () => void;
  children: React.ReactNode;
};

const PwaReturnsSheet: React.FC<PwaReturnsSheetProps> = ({
  title,
  closeLabel,
  pullDownLabel,
  onClose,
  children,
}) => {
  const dragZoneRef = React.useRef<HTMLDivElement>(null);
  const pwaSheetDragClose = usePwaBottomSheetDragClose(
    true,
    onClose,
    dragZoneRef
  );

  return (
    <>
      <button
        type="button"
        className="scope-pwa-returns-sheet-backdrop"
        aria-label={closeLabel}
        onClick={onClose}
      />
      <div
        className="scope-pwa-returns-sheet scope-pwa-bottom-sheet"
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div
          ref={dragZoneRef}
          className="scope-pwa-returns-sheet__chrome scope-pwa-bottom-sheet__drag-zone"
          aria-label={pullDownLabel}
          {...pwaSheetDragClose}
        >
          <div className="scope-pwa-bottom-sheet__drag-handle" aria-hidden />
          <div className="scope-pwa-returns-sheet__header">
            <strong>{title}</strong>
            <button
              type="button"
              className="scope-pwa-returns-sheet__close"
              onClick={onClose}
            >
              {closeLabel}
            </button>
          </div>
        </div>
        {children}
      </div>
    </>
  );
};

export const ReturnsPopover: React.FC<ReturnsPopoverProps> = ({
  returns,
  children,
  onClick,
  onCreateSubmission,
}) => {
  const [open, setOpen] = React.useState(false);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const currentUser = useSelector(getAuthUser);
  const pwaMobileNav = usePwaMobileNavEnabled();

  useOverlayBackHandler(
    open && pwaMobileNav,
    () => setOpen(false),
    'returns-sheet',
    pwaMobileNav
  );

  React.useEffect(() => {
    if (!open || !pwaMobileNav) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, pwaMobileNav]);

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
  };

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
    if (pwaMobileNav) {
      setOpen(true);
    }
  };

  const removeCountryFromReturnName = (mappingName: string): string => {
    if (!mappingName) return t('common.unknown');
    const parts = mappingName.split(' - ');
    return getReturnTypeNameLabel(
      parts.length > 1 ? parts.slice(1).join(' - ') : mappingName,
      t,
      i18n.language
    );
  };

  const getButtonTextAndAction = (
    returnItem: MappedReturn
  ): { text: string; isCreate: boolean } => ({
    text: getReturnActionLabel(returnItem, t, currentUser),
    isCreate: shouldCreateNewSubmission(returnItem),
  });

  const handleOpenReturn = (
    e: React.MouseEvent,
    returnItem: MappedReturn,
    connectionId: string,
    mappingId: string,
    dataLogId?: string
  ) => {
    e.stopPropagation();
    const { isCreate } = getButtonTextAndAction(returnItem);

    if (isCreate) {
      if (!isPwaTaxReturnCreationAllowed()) {
        setOpen(false);
        return;
      }
      if (onCreateSubmission) {
        onCreateSubmission(connectionId, mappingId);
      } else {
        console.error('onCreateSubmission callback not provided');
      }
    } else if (dataLogId) {
      const route = routes.vat.viewReportLink
        .replace(':connectionId', connectionId)
        .replace(':mappingId', mappingId)
        .replace(':dataLogId', dataLogId);
      navigate(route);
    }
    setOpen(false);
  };

  const maxRows = 10;
  const displayReturns = returns.slice(0, maxRows);
  const hasMore = returns.length > maxRows;

  const tableDataSource: ReturnRow[] = displayReturns.map((item, index) => {
    const returnName = removeCountryFromReturnName(
      item.mappingName || t('common.unknown')
    );
    const organisationName = item.organisation || t('common.unknown');
    const status = item.status || t('main.notStarted');
    const dueDate = item.nextDueDate
      ? formatDisplayDate(item.nextDueDate, 'DD/MM/YYYY', i18n.language)
      : '';
    const { text: buttonText, isCreate } = getButtonTextAndAction(item);

    return {
      key: item.mappingId || index,
      returnName,
      organisationName,
      status,
      originalStatus: item.status,
      dueDate,
      connectionId: item.connectionId,
      mappingId: item.mappingId,
      dataLogId: item.dataLog?.uuid,
      returnItem: item,
      buttonText,
      isCreate,
    };
  });

  const columns = [
    {
      title: t('dashboard.entity-name'),
      dataIndex: 'organisationName',
      key: 'organisationName',
      render: (text: string) => (
        <Text
          ellipsis={{ tooltip: true }}
          style={{
            fontSize: '12px',
            color: 'var(--color-text-muted, #5a6a6a)',
            maxWidth: 100,
          }}
        >
          {text}
        </Text>
      ),
    },
    {
      title: t('dashboard.return-name'),
      dataIndex: 'returnName',
      key: 'returnName',
      render: (text: string) => (
        <Text
          ellipsis={{ tooltip: true }}
          style={{
            fontSize: '12px',
            color: 'var(--color-text-base, #2e2e2e)',
            maxWidth: 110,
          }}
        >
          {text}
        </Text>
      ),
    },
    {
      title: t('main.dueDate'),
      dataIndex: 'dueDate',
      key: 'dueDate',
      render: (text: string) => (
        <Text
          style={{ fontSize: '12px', color: 'var(--color-text-muted, #5a6a6a)' }}
        >
          {text || '-'}
        </Text>
      ),
    },
    {
      title: t('main.status'),
      dataIndex: 'status',
      key: 'status',
      render: (text: string, record: ReturnRow) => {
        const statusValue = record.originalStatus || text || '';
        return <StatusGetter status={statusValue} smaller={true} />;
      },
    },
    {
      title: t('main.action-required'),
      key: 'action',
      render: (_: unknown, record: ReturnRow) => {
        if (
          !shouldShowReturnAction(record.returnItem, {
            allowCreate: isPwaTaxReturnCreationAllowed(),
          })
        ) {
          return null;
        }
        return (
          <ScopeButton
            type={
              isReturnActionPrimary(record.returnItem, currentUser)
                ? 'primary'
                : 'default'
            }
            width="70px"
            height="25px"
            fontSize="12px"
            lineheight="12px"
            onClick={(e: React.MouseEvent) =>
              handleOpenReturn(
                e,
                record.returnItem,
                record.connectionId,
                record.mappingId,
                record.dataLogId
              )
            }
          >
            {record.buttonText}
          </ScopeButton>
        );
      },
    },
  ];

  const emptyState = (
    <Text
      style={{
        color: 'var(--color-text-muted, #5a6a6a)',
        display: 'block',
        padding: '16px',
      }}
    >
      {t('organisation.no-returns-found')}
    </Text>
  );

  const moreLabel = hasMore ? (
    <Text
      style={{
        color: 'var(--color-text-muted, #5a6a6a)',
        display: 'block',
        padding: '8px 0',
        textAlign: 'center',
        fontSize: '12px',
      }}
    >
      +{returns.length - maxRows} more
    </Text>
  ) : null;

  const desktopContent = (
    <div style={{ maxWidth: 700, padding: '12px' }}>
      {returns.length === 0 ? (
        emptyState
      ) : (
        <div
          style={{ maxHeight: '350px', overflowY: 'auto', overflowX: 'hidden' }}
        >
          <ScopeTable
            tableDataSource={tableDataSource}
            columns={columns}
            pagination={false}
            rowsPerPage={tableDataSource.length}
            enableColumnsCustomization={false}
            rowHeight={35}
            fontSize={12}
            showSizeChanger={false}
            getNewPageSize={() => {}}
            transactionType=""
            reportId=""
          />
          {moreLabel}
        </div>
      )}
    </div>
  );

  const pwaCards = (
    <div className="scope-pwa-returns-sheet__list">
      {returns.length === 0
        ? emptyState
        : tableDataSource.map((record) => (
            <div className="scope-pwa-returns-sheet__card" key={record.key}>
              <div className="scope-pwa-returns-sheet__card-title">
                {record.returnName}
              </div>
              {record.organisationName ? (
                <div className="scope-pwa-returns-sheet__card-entity">
                  {record.organisationName}
                </div>
              ) : null}
              <div className="scope-pwa-returns-sheet__card-row">
                <span>{t('main.dueDate')}</span>
                <strong>{record.dueDate || '-'}</strong>
              </div>
              <div className="scope-pwa-returns-sheet__card-row">
                <span>{t('main.status')}</span>
                <StatusGetter
                  status={record.originalStatus || record.status || ''}
                  smaller
                />
              </div>
              <div className="scope-pwa-returns-sheet__card-action">
                {shouldShowReturnAction(record.returnItem, {
                  allowCreate: isPwaTaxReturnCreationAllowed(),
                }) ? (
                  <ScopeButton
                    type={
                      isReturnActionPrimary(record.returnItem, currentUser)
                        ? 'primary'
                        : 'default'
                    }
                    width="100%"
                    height="36px"
                    fontSize="13px"
                    lineheight="16px"
                    onClick={(e: React.MouseEvent) =>
                      handleOpenReturn(
                        e,
                        record.returnItem,
                        record.connectionId,
                        record.mappingId,
                        record.dataLogId
                      )
                    }
                  >
                    {record.buttonText}
                  </ScopeButton>
                ) : null}
              </div>
            </div>
          ))}
      {moreLabel}
    </div>
  );

  if (pwaMobileNav) {
    const sheet =
      open && typeof document !== 'undefined'
        ? createPortal(
            <PwaReturnsSheet
              title={t('dashboard.card-title-returns-by-due-date')}
              closeLabel={t('common.close')}
              pullDownLabel={t('organisation.pull-down-to-close')}
              onClose={() => setOpen(false)}
            >
              {pwaCards}
            </PwaReturnsSheet>,
            document.body
          )
        : null;

    return (
      <>
        <div
          onClick={handleClick}
          style={{ cursor: 'pointer', display: 'block' }}
          role="button"
          tabIndex={0}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              handleClick();
            }
          }}
        >
          {children}
        </div>
        {sheet}
      </>
    );
  }

  return (
    <Popover
      content={desktopContent}
      trigger={['hover', 'click']}
      open={open}
      onOpenChange={handleOpenChange}
      overlayClassName="returns-popover-overlay"
      overlayStyle={{ maxWidth: 750, zIndex: 9999 }}
      getPopupContainer={() => document.body}
      mouseEnterDelay={0.1}
      mouseLeaveDelay={0.1}
      destroyOnHidden={true}
    >
      <div
        onClick={handleClick}
        style={{ cursor: 'pointer', display: 'block' }}
      >
        {children}
      </div>
    </Popover>
  );
};
